// Types for the mobile app and the website's Daily log: app logins (user
// name + password), dose logs, blood pressure, app events and phones.

/** User names are stored as this hidden address: <username>@users.bhorosha.app */
export const USERNAME_DOMAIN = 'users.bhorosha.app';
export const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{2,29}$/;

/** What to sign in with: an email as typed, or a user name turned into its hidden address. */
export function loginEmail(userNameOrEmail: string): string {
  const s = String(userNameOrEmail || '').trim().toLowerCase();
  return s.includes('@') ? s : `${s}@${USERNAME_DOMAIN}`;
}
/** "ammu@users.bhorosha.app" -> "ammu"; other emails unchanged. */
export function displayLogin(email: string): string {
  const e = String(email || '');
  return e.endsWith('@' + USERNAME_DOMAIN) ? e.slice(0, -(USERNAME_DOMAIN.length + 1)) : e;
}
export const isUsernameLogin = (email: string) => String(email || '').endsWith('@' + USERNAME_DOMAIN);

export interface AppLogin {
  userId: string;
  username: string;
  displayName: string;
  disabled: boolean;
  lastSignInAt: string | null;
  createdAt: string;
}
export type AppLoginAction =
  | { action: 'family_create_login'; familyId: string; username: string; name: string; password?: string; role: 'editor' | 'viewer'; allPatients: boolean; patientIds: string[] }
  | { action: 'family_reset_password'; familyId: string; userId: string; password?: string }
  | { action: 'family_disable_login'; familyId: string; userId: string; disabled: boolean }
  | { action: 'family_delete_login'; familyId: string; userId: string };
export interface AppLoginResult { userId?: string; username?: string; password?: string }

export type DoseSource = 'medicine' | 'injection';
export type LoggedFrom = 'mobile' | 'notification' | 'web';
export type DoseLogStatus = 'taken' | 'skipped' | 'missed';

export interface DoseChange { at: string; by: string; from: LoggedFrom; device?: string | null; old: DoseLogStatus | null; new: DoseLogStatus | null; old_taken_at?: string | null; taken_at?: string | null }

export interface DoseLog {
  patientId: string;
  id: string;
  source: DoseSource;
  sourceId: string;
  medicineId: string | null;
  doseDate: string;
  slot: string;
  plannedTime: string | null;
  name: string | null;
  amount: number;
  unit: string | null;
  status: DoseLogStatus;
  takenAt: string | null;
  reason: string | null;
  note: string | null;
  loggedBy: string | null;
  loggedFrom: LoggedFrom | null;
  changes: DoseChange[];
  updatedAt: string;
}

export interface MarkDoseArgs {
  patientId: string;
  source: DoseSource;
  sourceId: string;
  medicineId: string | null;
  date: string;
  slot: string;
  plannedTime: string;
  name: string;
  amount: number;
  unit: string;
  /** null = Undo */
  status: DoseLogStatus | null;
  takenAt?: string | null;
  reason?: string | null;
  note?: string | null;
  from: LoggedFrom;
  deviceId?: string | null;
}

export type BpLevelName = 'normal' | 'high' | 'very_high' | 'low';
export interface BpReading {
  patientId: string;
  id: string;
  measuredAt: string;
  systolic: number;
  diastolic: number;
  pulse: number | null;
  arm?: 'left' | 'right' | null;
  position?: 'sitting' | 'lying' | 'standing' | null;
  level: BpLevelName;
  note?: string | null;
  loggedBy?: string | null;
  loggedFrom?: LoggedFrom | null;
}

export interface AppEvent {
  id: string;
  patientId: string | null;
  userId?: string;
  deviceId: string | null;
  at: string;
  kind: string;
  data: Record<string, unknown>;
}

export interface AppDevice {
  userId?: string;
  deviceId: string;
  displayName?: string | null;
  platform?: string | null;
  model?: string | null;
  appVersion?: string | null;
  notifications?: 'allowed' | 'blocked' | 'unknown' | null;
  exactAlarms?: boolean | null;
  batteryOptimised?: boolean | null;
  pushToken?: string | null;
  lastSeenAt?: string;
  lastSyncAt?: string | null;
  remindersUntil?: string | null;
  /** Alerts turned on / off on this phone (Settings → Alerts). */
  alertPrefs?: AlertPrefs | null;
}

/** Which alerts ring on a phone. Missing = on. */
export interface AlertPrefs { doses?: boolean; injections?: boolean; bp?: boolean; visits?: boolean; vaccines?: boolean }

export interface MonitorSnapshot { doses: DoseLog[]; bp: BpReading[]; events: AppEvent[]; devices: AppDevice[] }

/** One medicine in the shared Medicine Library (kept by super admins). */
export interface CatalogMedicine { id: string; name: string; strength: string; unit: string; type: string; generic: string; company: string }
export type CatalogInput = Omit<CatalogMedicine, 'id'> & { id?: string };
/** Same name + strength = the same medicine in the shared Medicine Library. */
export const catalogKey = (m: { name: string; strength?: string }) => `${m.name.trim().toLowerCase()}|${String(m.strength || '').trim().toLowerCase()}`;
export function cleanCatalog(m: CatalogInput): CatalogInput {
  const s = (v: unknown) => String(v ?? '').trim();
  return { ...(m.id ? { id: m.id } : {}), name: s(m.name), strength: s(m.strength), unit: s(m.unit), type: s(m.type).toLowerCase(), generic: s(m.generic), company: s(m.company) };
}


/** Methods for the mobile app and the Daily log (both backends implement them). */
export interface MonitoringBackend {
  listAppLogins(familyId: string): Promise<AppLogin[]>;
  appLoginAction(a: AppLoginAction): Promise<AppLoginResult>;
  /** Dose logs and BP readings for dates from..to (inclusive), events in that period, and every phone. */
  loadMonitor(familyId: string, fromISO: string, toISO: string, patientIds?: string[]): Promise<MonitorSnapshot>;
  markDose(familyId: string, a: MarkDoseArgs): Promise<{ onHand: number | null }>;
  saveBp(familyId: string, r: BpReading): Promise<void>;
  /** Blood pressure readings of one person, dates from..to (inclusive), newest first. */
  loadBpReadings(familyId: string, patientId: string, fromISO: string, toISO: string): Promise<BpReading[]>;
  /** Remove a reading: the owner any reading, others only their own. */
  deleteBp(familyId: string, patientId: string, id: string): Promise<void>;
  logEvents(familyId: string, events: AppEvent[]): Promise<void>;
  upsertDevice(familyId: string, d: AppDevice): Promise<void>;
  /** The shared Medicine Library (everyone signed in can read it). */
  listCatalog(): Promise<CatalogMedicine[]>;
  /** Super admin: add or change medicines (same name + strength = the same medicine). Returns how many were added / updated. */
  adminSaveCatalog(items: CatalogInput[], updateMatches: boolean): Promise<{ added: number; updated: number; skipped: number }>;
  /** Super admin: delete medicines from the shared list. */
  adminDeleteCatalog(ids: string[]): Promise<void>;
  /** Take a phone off the family's list and sign it out (owner or super admin). Its log entries stay. */
  removeDevice(familyId: string, userId: string, deviceId: string): Promise<void>;
  /** Phone: has the family owner removed (signed out) this phone? Clears the request when found. */
  takeSignOutRequest(deviceId: string): Promise<boolean>;
  /** Called (no details) whenever a dose log, BP reading, event or phone changes. */
  subscribeMonitor(familyId: string, onChange: () => void): () => void;
}

/** Local day boundaries for a "YYYY-MM-DD" date range, as ISO timestamps. */
export function dayRangeISO(fromISO: string, toISO: string): { from: string; to: string } {
  const [fy, fm, fd] = fromISO.split('-').map(Number);
  const [ty, tm, td] = toISO.split('-').map(Number);
  return { from: new Date(fy, fm - 1, fd, 0, 0, 0).toISOString(), to: new Date(ty, tm - 1, td + 1, 0, 0, 0).toISOString() };
}
